import { Edge, Node as ElkNode } from '../../../libs/graph'
import { LineageGraph } from '../../types/api'

import { JobOrDataset, LineageDataset, LineageJob, LineageNode, PipelineJob } from '../../types/lineage'
import { Nullable } from '../../types/util/Nullable'
import { TableLevelNodeData } from './nodes'
import { theme } from '../../helpers/theme'
import { isPipelineJob, getTasksForPipeline, createPipelineJob } from '../../helpers/nodes'

/**
 * Recursively trace the `inEdges` and `outEdges` of the current node to find all connected downstream column nodes
 * @param lineageGraph
 * @param currentGraphNode
 */
export const findDownstreamNodes = (
  lineageGraph: LineageGraph,
  currentGraphNode: Nullable<string>
): LineageNode[] => {
  if (!currentGraphNode) return []
  const currentNode = lineageGraph.graph.find((node) => node.id === currentGraphNode)
  if (!currentNode) return []
  const connectedNodes: LineageNode[] = []
  const visitedNodes: string[] = []
  const queue: LineageNode[] = [currentNode]

  while (queue.length) {
    const currentNode = queue.shift()
    if (!currentNode) continue
    if (visitedNodes.includes(currentNode.id)) continue
    visitedNodes.push(currentNode.id)
    connectedNodes.push(currentNode)
    queue.push(
      ...currentNode.outEdges
        .map((edge) => lineageGraph.graph.find((n) => n.id === edge.destination))
        .filter((item): item is LineageNode => !!item)
    )
  }
  return connectedNodes
}
/**
 * Recursively trace the `inEdges` and `outEdges` of the current node to find all connected upstream column nodes
 * @param lineageGraph
 * @param currentGraphNode
 */
export const findUpstreamNodes = (
  lineageGraph: LineageGraph,
  currentGraphNode: Nullable<string>
): LineageNode[] => {
  if (!currentGraphNode) return []
  const currentNode = lineageGraph.graph.find((node) => node.id === currentGraphNode)
  if (!currentNode) return []
  const connectedNodes: LineageNode[] = []
  const visitedNodes: string[] = []
  const queue: LineageNode[] = [currentNode]

  while (queue.length) {
    const currentNode = queue.shift()
    if (!currentNode) continue
    if (visitedNodes.includes(currentNode.id)) continue
    visitedNodes.push(currentNode.id)
    connectedNodes.push(currentNode)
    queue.push(
      ...currentNode.inEdges
        .map((edge) => lineageGraph.graph.find((n) => n.id === edge.origin))
        .filter((item): item is LineageNode => !!item)
    )
  }
  return connectedNodes
}

export const transformLineageGraphWithPipelines = (
  lineageGraph: LineageGraph,
  showPipelines: boolean = false
): LineageGraph => {
  if (!showPipelines) {
    return lineageGraph
  }

  const jobs = lineageGraph.graph
    .filter(node => node.type === 'JOB')
    .map(node => node.data as LineageJob)
  
  const datasets = lineageGraph.graph.filter(node => node.type === 'DATASET')
  
  // Find all pipeline names (jobs without dots)
  const pipelineNames = jobs
    .filter(job => isPipelineJob(job.name))
    .map(job => job.name)
  
  // Create pipeline jobs
  const pipelineJobs: LineageNode[] = []
  const tasksToHide: string[] = []
  
  for (const pipelineName of pipelineNames) {
    const pipelineJob = createPipelineJob(jobs, pipelineName)
    if (pipelineJob) {
      const tasks = getTasksForPipeline(jobs, pipelineName)
      tasksToHide.push(...tasks.map(task => task.name))
      
      pipelineJobs.push({
        id: `pipeline:${pipelineJob.namespace}:${pipelineJob.name}`,
        type: 'PIPELINE',
        data: pipelineJob,
        inEdges: [],
        outEdges: []
      })
    }
  }
  
  // Filter out task jobs and keep only pipeline jobs and datasets
  const filteredJobs = lineageGraph.graph.filter(node => 
    node.type !== 'JOB' || !tasksToHide.includes((node.data as LineageJob).name)
  )
  
  // Combine pipeline jobs with datasets and remaining jobs
  const newGraph = [...filteredJobs, ...pipelineJobs]
  
  // Update edges to connect to pipeline jobs instead of task jobs
  newGraph.forEach(node => {
    node.inEdges = node.inEdges.map(edge => {
      const originNode = lineageGraph.graph.find(n => n.id === edge.origin)
      if (originNode?.type === 'JOB') {
        const originJob = originNode.data as LineageJob
        if (tasksToHide.includes(originJob.name)) {
          const pipelineName = originJob.name.split('.')[0]
          return {
            ...edge,
            origin: `pipeline:${originJob.namespace}:${pipelineName}`
          }
        }
      }
      return edge
    })
    
    node.outEdges = node.outEdges.map(edge => {
      const destinationNode = lineageGraph.graph.find(n => n.id === edge.destination)
      if (destinationNode?.type === 'JOB') {
        const destinationJob = destinationNode.data as LineageJob
        if (tasksToHide.includes(destinationJob.name)) {
          const pipelineName = destinationJob.name.split('.')[0]
          return {
            ...edge,
            destination: `pipeline:${destinationJob.namespace}:${pipelineName}`
          }
        }
      }
      return edge
    })
  })
  
  return {
    graph: newGraph
  }
}

export const createElkNodes = (
  lineageGraph: LineageGraph,
  currentGraphNode: Nullable<string>,
  isCompact: boolean,
  isFull: boolean,
  collapsedNodes: Nullable<string>,
  showPipelines: boolean = false
) => {
  // Transform the graph to aggregate pipelines if requested
  const transformedGraph = transformLineageGraphWithPipelines(lineageGraph, showPipelines)
  
  const downstreamNodes = findDownstreamNodes(transformedGraph, currentGraphNode)
  const upstreamNodes = findUpstreamNodes(transformedGraph, currentGraphNode)

  const nodes: ElkNode<JobOrDataset, TableLevelNodeData>[] = []
  const edges: Edge[] = []

  const collapsedNodesAsArray = collapsedNodes?.split(',')

  const filteredGraph = transformedGraph.graph.filter((node) => {
    if (isFull) return true
    return (
      downstreamNodes.includes(node) || upstreamNodes.includes(node) || node.id === currentGraphNode
    )
  })

  for (const node of filteredGraph) {
    edges.push(
      ...node.outEdges
        .filter((edge) => filteredGraph.find((n) => n.id === edge.destination))
        .map((edge) => {
          return {
            id: `${edge.origin}:${edge.destination}`,
            sourceNodeId: edge.origin,
            targetNodeId: edge.destination,
            color:
              downstreamNodes.includes(node) || upstreamNodes.includes(node)
                ? theme.palette.primary.main
                : theme.palette.grey[400],
          }
        })
    )

    if (node.type === 'JOB') {
      nodes.push({
        id: node.id,
        kind: node.type,
        width: 112,
        height: 24,
        data: {
          job: node.data as LineageJob,
        },
      })
    } else if (node.type === 'PIPELINE') {
      nodes.push({
        id: node.id,
        kind: node.type,
        width: 112,
        height: 32,
        data: {
          pipeline: node.data as PipelineJob,
        },
      })
    } else if (node.type === 'DATASET') {
      const data = node.data as LineageDataset
      nodes.push({
        id: node.id,
        kind: node.type,
        width: 112,
        height:
          isCompact || collapsedNodesAsArray?.includes(node.id) ? 24 : 34 + data.fields.length * 10,
        data: {
          dataset: data,
        },
      })
    }
  }
  return { nodes, edges }
}
